import { create } from 'qrcode';

/**
 * QR branding renderers.
 *
 * Two styles live here, both built from the same QR matrix.
 *
 * Halftone follows github.com/nythrox/halftone-qrcode: the picture is the
 * background, pixelated hard and thresholded to two tones, and each module
 * carries its value as a small square at its cell centre (function patterns
 * stay solid). Keeping the module as a dot instead of sub-dividing the cell is
 * what lets the picture stay legible.
 *
 * Photo keeps the picture at full resolution, washed to a pale tint, with the
 * dark modules painted solid over it.
 *
 * Both render at error correction H, so the texture the picture adds sits
 * inside what the code can recover.
 */

function hexToRgb(hex: string): [number, number, number] {
  const raw = hex.replace('#', '');
  const full =
    raw.length === 3
      ? raw
          .split('')
          .map((part) => part + part)
          .join('')
      : raw;
  return [parseInt(full.slice(0, 2), 16), parseInt(full.slice(2, 4), 16), parseInt(full.slice(4, 6), 16)];
}

/** Draw the image over a size x size square at (x, y), centre-cropped (cover). */
function drawCover(ctx: CanvasRenderingContext2D, image: HTMLImageElement, x: number, y: number, size: number): void {
  const sourceWidth = image.naturalWidth || image.width;
  const sourceHeight = image.naturalHeight || image.height;
  const scale = Math.max(size / sourceWidth, size / sourceHeight);
  const width = sourceWidth * scale;
  const height = sourceHeight * scale;
  ctx.drawImage(image, x + (size - width) / 2, y + (size - height) / 2, width, height);
}

// ------------------------------------------------------------------ halftone

// A cell is 3 dots wide, so the module dot covers a third of it and the picture
// fills the rest. Three is the smallest that still scans reliably.
const HALFTONE_BLOCK = 3;
const HALFTONE_PIXEL = 8;
// Keep only this fraction of the picture's resolution - a coarse mosaic reads
// behind the dots where fur-level detail would only add noise.
const HALFTONE_PIXELATION = 0.925;
const HALFTONE_THRESHOLD = 127;

/**
 * Render the payload as a halftone of the image and return a PNG data URL.
 *
 * The picture is flattened to two tones (white plus darkColor) before the
 * module dots are drawn, so the code and the picture share one palette.
 */
export function renderHalftoneQr(payload: string, image: HTMLImageElement, darkColor: string): string {
  const qr = create(payload, { errorCorrectionLevel: 'H' });
  const size = qr.modules.size;
  const data = qr.modules.data;
  const reserved = qr.modules.reservedBit;

  const pixel = HALFTONE_PIXEL;
  const block = pixel * HALFTONE_BLOCK;
  const side = size * block;

  const canvas = document.createElement('canvas');
  canvas.width = side;
  canvas.height = side;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('Could not create a 2D context for the halftone QR.');

  // 1. Picture as the background, pixelated then thresholded.
  drawCover(ctx, image, 0, 0, side);
  const scaled = Math.max(1, Math.round(side * (1 - HALFTONE_PIXELATION)));
  const small = document.createElement('canvas');
  small.width = scaled;
  small.height = scaled;
  const smallCtx = small.getContext('2d');
  if (!smallCtx) throw new Error('Could not create a 2D context for the halftone image.');
  smallCtx.imageSmoothingEnabled = false;
  smallCtx.drawImage(canvas, 0, 0, scaled, scaled);
  ctx.imageSmoothingEnabled = false;
  ctx.clearRect(0, 0, side, side);
  ctx.drawImage(small, 0, 0, side, side);

  const [darkR, darkG, darkB] = hexToRgb(darkColor);
  const picture = ctx.getImageData(0, 0, side, side);
  const pixels = picture.data;
  for (let i = 0; i < pixels.length; i += 4) {
    const grey = (pixels[i] + pixels[i + 1] + pixels[i + 2]) / 3;
    if (grey > HALFTONE_THRESHOLD) {
      pixels[i] = pixels[i + 1] = pixels[i + 2] = 255;
    } else {
      pixels[i] = darkR;
      pixels[i + 1] = darkG;
      pixels[i + 2] = darkB;
    }
  }
  ctx.putImageData(picture, 0, 0);

  // 2. The module value as a dot at each cell centre; function patterns solid.
  const white = '#ffffff';
  for (let row = 0; row < size; row += 1) {
    for (let col = 0; col < size; col += 1) {
      const index = row * size + col;
      ctx.fillStyle = data[index] === 1 ? darkColor : white;
      const x = col * block;
      const y = row * block;
      if (reserved[index] === 1) {
        ctx.fillRect(x, y, block, block);
      } else {
        ctx.fillRect(x + pixel, y + pixel, pixel, pixel);
      }
    }
  }

  return canvas.toDataURL('image/png');
}

// --------------------------------------------------------------------- photo

const PHOTO_MODULE_PX = 10;
const PHOTO_QUIET_MODULES = 4;
// How much of the picture's darkness survives the wash (0 white .. 1 full).
// Kept low so a light module never gets dark enough to confuse a scanner.
const PHOTO_WASH = 0.3;

/**
 * Draw the payload over the picture itself.
 *
 * Unlike the halftone, the image keeps its resolution: it is washed to a pale
 * grey tint and the dark QR modules are drawn solid on top. It reads at a
 * glance, and because the wash lifts even a black pixel well above mid grey the
 * light modules stay unambiguously light.
 */
export function renderPhotoQr(payload: string, image: HTMLImageElement, darkColor: string): string {
  const qr = create(payload, { errorCorrectionLevel: 'H' });
  const size = qr.modules.size;
  const data = qr.modules.data;

  const quiet = PHOTO_QUIET_MODULES * PHOTO_MODULE_PX;
  const code = size * PHOTO_MODULE_PX;
  const side = code + quiet * 2;

  const canvas = document.createElement('canvas');
  canvas.width = side;
  canvas.height = side;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('Could not create a 2D context for the photo QR.');

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, side, side);
  drawCover(ctx, image, quiet, quiet, code);

  const picture = ctx.getImageData(quiet, quiet, code, code);
  const pixels = picture.data;
  for (let i = 0; i < pixels.length; i += 4) {
    const luminance = 0.299 * pixels[i] + 0.587 * pixels[i + 1] + 0.114 * pixels[i + 2];
    const pale = 255 - (255 - luminance) * PHOTO_WASH;
    pixels[i] = pale;
    pixels[i + 1] = pale;
    pixels[i + 2] = pale;
    pixels[i + 3] = 255;
  }
  ctx.putImageData(picture, quiet, quiet);

  ctx.fillStyle = darkColor;
  for (let row = 0; row < size; row += 1) {
    for (let col = 0; col < size; col += 1) {
      if (data[row * size + col] === 1) {
        ctx.fillRect(quiet + col * PHOTO_MODULE_PX, quiet + row * PHOTO_MODULE_PX, PHOTO_MODULE_PX, PHOTO_MODULE_PX);
      }
    }
  }

  return canvas.toDataURL('image/png');
}
